import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import axios from "axios";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";

const OnBoarding_WhatsAppLink = forwardRef((props, ref) => {
  const [alertMessage, setAlertMessage] = useState("");
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isWaitingForAuthentication, setIsWaitingForAuthentication] =
    useState(false);
  const [verificationCode, setVerificationCode] = useState("");

  useImperativeHandle(ref, () => ({ validate }));

  useEffect(() => {
    const fetchVerificationCode = async () => {
      try {
        const response = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/generate-auth-code`,
          { mobile: props.mobile },
          {
            headers: {
              "x-api-key": process.env.REACT_APP_BACKEND_API_KEY,
            },
          }
        );

        const { code } = response.data;
        setVerificationCode(code);
        console.log("Fetched verification code:", code);

        setIsWaitingForAuthentication(true);
        pollVerificationStatus();
      } catch (error) {
        console.error("Error fetching verification code:", error);
        setAlertMessage("Failed to generate the verification code.");
      }
    };

    fetchVerificationCode();
  }, [props.mobile]);

  const validate = () => {
    if (verificationCode) {
      setAlertMessage("");
      return true;
    }
    setAlertMessage("Verification code is missing or invalid.");
    return false;
  };

  const handleWhatsAppClick = () => {
    if (!verificationCode) {
      setAlertMessage("Verification code is missing.");
      return;
    }
    const isDev = process.env.REACT_APP_ENVIRONMENT === "development";
    const codeToUse = isDev ? `dev-${verificationCode}` : verificationCode;
    if (isDev) {
      console.warn("Running in development mode, using mock verification code.");
      console.log("Using development verification code:", codeToUse);
    }

    const prepopulatedMessage = `Hi, my verification code is: ${codeToUse}`;
    const whatsappUrl = `https://wa.me/${process.env.REACT_APP_WHATSAPP_NUMBER}?text=${encodeURIComponent(
      prepopulatedMessage
    )}`;

    window.open(whatsappUrl, "_blank");
    console.log("Opened WhatsApp with message:", prepopulatedMessage);
  };

  const pollVerificationStatus = () => {
    const interval = setInterval(async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/check-auth-status`,
          {
            params: { mobile: props.mobile },
            headers: {
              "x-api-key": process.env.REACT_APP_BACKEND_API_KEY,
            },
          }
        );

        if (response.data.verified) {
          console.log("Verification successful!");
          setIsAuthenticated(true);
          setIsWaitingForAuthentication(false);
          setAlertMessage("Verification successful!");
          clearInterval(interval);

          if (props.onVerificationSuccess) {
            props.onVerificationSuccess();
          }
        }
      } catch (error) {
        console.error("Error checking verification status:", error);
        setAlertMessage("Error checking authentication status.");
      }
    }, 5000);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        mt: 3,
        pb: 3,
      }}
    >
      <Typography sx={{ mb: 2 }}>
        To complete your registration, please tap on the WhatsApp button below
        and send the pre-populated verification message.
      </Typography>

      {isWaitingForAuthentication && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            mb: 2,
          }}
        >
          <CircularProgress size={24} sx={{ color: "#25D366" }} />
          <Typography sx={{ mt: 1, color: "#555" }}>
            Awaiting WhatsApp authentication...
          </Typography>
        </Box>
      )}

      {!isAuthenticated ? (
        <Button
          variant="contained"
          onClick={handleWhatsAppClick}
          startIcon={<WhatsAppIcon sx={{ color: "#fff" }} />}
          sx={{
            backgroundColor: "#25D366",
            color: "#fff",
            "&:hover": { backgroundColor: "#1DA955" },
            boxShadow: "none",
            padding: 1.5,
            mt: 2,
          }}
        >
          Authenticate with WhatsApp
        </Button>
      ) : (
        <Typography sx={{ mt: 2, color: "green", fontWeight: "bold" }}>
          Authentication successful!
        </Typography>
      )}

      {alertMessage && (
        <Typography sx={{ mt: 2, color: "red", textAlign: "center" }}>
          {alertMessage}
        </Typography>
      )}
    </Box>
  );
});

export default OnBoarding_WhatsAppLink;
